import { normalizePersonName, stripNonNameCharacters } from "@/utils/normalizePersonName";

/**
 * The keystroke-level guards for the first-name and email fields, shared by
 * every newsletter form (the footer band, the home-page section and the
 * /newsletter page) so all three behave like the create-account modal.
 *
 * Each returns a `transformValue` for `FormikInput`. They refuse a character
 * by not letting it into the field, and call `onReject` so the caller can
 * shake — because a field that silently swallows the space bar reads as a
 * broken keyboard, not as a rule. That was the state of all three newsletter
 * forms: an `onKeyDown` handler cancelled the space and said nothing.
 *
 * Rejection is the ONLY signal; there is no error message and nothing to mark
 * invalid, since the character never lands in the value.
 */

/**
 * First name, identical to the create-account modal: refuse anything a name
 * cannot contain — digits, dots, commas, spaces, symbols — and capitalise what
 * remains as they type ("patrick" → "Patrick", "anne-marie" → "Anne-Marie").
 *
 * The capitalisation is a courtesy, not the enforcement: the server applies the
 * same rule in `common/validation/normalize-name.ts`, so subscribing from
 * anywhere else still produces "Patrick".
 */
export const makeNameFieldTransform =
  (onReject: () => void) =>
  (raw: string): string => {
    const kept = stripNonNameCharacters(raw);
    if (kept.length !== raw.length) onReject();
    return normalizePersonName(kept);
  };

/**
 * Email: refuse whitespace anywhere. An address cannot contain any, and letting
 * one through means the customer gets a format error at the bottom of the form
 * for a space they cannot see. Shaking at the moment they press it says the same
 * thing, sooner, at the field it concerns.
 *
 * Whitespace only — every other character is left alone. Which strings are valid
 * addresses is the submit-time schema's job, and refusing characters mid-typing
 * would fight anyone with an unusual but legal address.
 */
export const makeEmailFieldTransform =
  (onReject: () => void) =>
  (raw: string): string => {
    const kept = raw.replace(/\s/g, "");
    if (kept.length !== raw.length) onReject();
    return kept;
  };
